import React, { useEffect, useMemo, useState } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardMedia from '@mui/material/CardMedia';
import Typography from '@mui/material/Typography';
import { CardActionArea } from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import LocationOnRoundedIcon from '@mui/icons-material/LocationOnRounded';

import { Link } from 'react-router-dom';

import './styles.css';

const filter = [
  { name: 'All' },
  { name: 'Resort' },
  { name: 'Hotels' },
  { name: 'Lodges' },
  { name: 'Luxury' },
];

// Because this is an inframe, so the SSR mode doesn't not do well here.
// It will work on real devices.
const HomeCardSection = ({ title, serviceProviders }) => {
  const [screenSize, setScreenSize] = useState(getCurrentDimension());

  function getCurrentDimension() {
    return {
      width: window.innerWidth,
      height: window.innerHeight,
    };
  }

  useEffect(() => {
    const updateDimension = () => {
      setScreenSize(getCurrentDimension());
    };
    window.addEventListener('resize', updateDimension);

    return () => {
      window.removeEventListener('resize', updateDimension);
    };
  }, [screenSize]);

  // Derived from the prop, not copied into state on mount — the copy this
  // replaces had an empty dependency array and so ignored every realtime
  // update after the first one.
  const providersList = useMemo(
    () => (Array.isArray(serviceProviders) ? serviceProviders : []),
    [serviceProviders]
  );

  const hasProviders = providersList.length > 0;

  // react-grid-carousel (abandoned, React 16 peer) laid these out as pages of
  // `cols` x 2 and handled the paging itself. Embla has no notion of rows, so
  // the grid is rebuilt in CSS and each slide carries one full page of cards.
  // The class names are scoped rather than the bare `.embla*` ones, because
  // those are global and pin a hero height (see MainCarousel/styles.css).
  const cols = screenSize.width < 680 ? 2 : 4;

  const pages = useMemo(() => {
    const perPage = cols * 2;
    const chunks = [];
    for (let i = 0; i < providersList.length; i += perPage) {
      chunks.push(providersList.slice(i, i + perPage));
    }
    return chunks;
  }, [providersList, cols]);

  const [emblaRef] = useEmblaCarousel({ loop: true });

  // Nothing to render and no section heading: render nothing rather than an empty
  // carousel / dead filter row. NOTE: both call sites (Destinations, SearchAndFilter)
  // currently mount this without a `serviceProviders` prop, so it is always empty
  // there — flagged in CHECKPOINT_REPORT.md (wiring data is logic, out of scope).
  if (!hasProviders && !title) return null;

  return (
    <Box className="home-section" sx={{ mx: { xs: 1, sm: 0 }, mb: 2 }}>
      {title && (
        <Typography component="h2" className="txt--gray">
          {title}
        </Typography>
      )}

      <Box className="filter-group" sx={{ mb: 1.5 }}>
        {filter.map((f) => (
          <Button key={`${f.name}-home-card`} className="filter" variant="outlined">
            {f.name}
          </Button>
        ))}
      </Box>

      {hasProviders ? (
        <Box sx={{ width: '100%' }}>
          <div className="home-card-embla" ref={emblaRef}>
            <div className="home-card-embla__container">
              {pages.map((page, pageIndex) => (
                <div
                  className="home-card-embla__slide"
                  key={`home-card-page-${pageIndex}`}
                  style={{ gridTemplateColumns: `repeat(${cols}, 1fr)` }}
                >
                  {page.map((provider, i) => (
                    <Card
                      key={`filter-${pageIndex}-${i}`}
                      component={Link}
                      to={`/listing/${provider.id}`}
                      sx={{ boxShadow: 'none', borderRadius: 2, textDecoration: 'none', color: 'inherit', display: 'block' }}
                    >
                      <CardActionArea component="div">
                        <CardMedia
                          component="img"
                          height="320"
                          image={provider.images?.[0] || ''}
                          alt={provider.name}
                        />
                        <CardContent>
                          <Typography
                            variant="subtitle1"
                            component="div"
                            sx={{ display: 'flex', alignItems: 'center', color: 'text.primary' }}
                          >
                            <LocationOnRoundedIcon
                              sx={{ fontSize: '1rem', mr: 0.5, color: 'text.secondary', flexShrink: 0 }}
                            />
                            {provider.name}
                          </Typography>
                        </CardContent>
                      </CardActionArea>
                    </Card>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </Box>
      ) : (
        <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
          No places to show yet.
        </Typography>
      )}
    </Box>
  );
};

export default HomeCardSection;
